/**
 * AccessGovernance.tsx
 * Visibility & investor-access governance UI primitives. Presentation-only.
 * All gating is still ENFORCED by geo-api on the server. These components
 * surface that gating to the user in a consistent, reusable way.
 *
 * Brand tokens: Royal Blue, Window Gray RAL 7040, Nikon Yellow, Viridian Green, Coffee Brown.
 */
import React from 'react';
import { Lock, ShieldAlert, ShieldCheck, FileSignature, Eye, Download, Clock, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
} from '@/components/eds';
import type { GeoClassification, GeoDataset, GisLayer, DisclosureRequestStatus } from '@/services/gisService';
import type { InvestorStatus, InvestorProfileSummary, ReviewTimelineEntry, DocumentAccessEntry } from '@/services/investorService';

/* ───────── Classification labels ───────── */

const CLASS_META: Record<
  GeoClassification,
  {
    label: string;
    tone: 'neutral' | 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'gold';
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  public:               { label: 'Public',              tone: 'success', icon: Eye },
  restricted:           { label: 'Restricted',          tone: 'warning', icon: ShieldAlert },
  confidential:         { label: 'Confidential',        tone: 'danger',  icon: Lock },
  executive_only:       { label: 'Executive Only',      tone: 'neutral', icon: Lock },
  ministry_restricted:  { label: 'Ministry Restricted', tone: 'info',    icon: ShieldAlert },
  investor_limited:     { label: 'Investor Limited',    tone: 'gold',    icon: ShieldCheck },
  NDA_required:         { label: 'NDA Required',        tone: 'warning', icon: FileSignature },
  MOU_required:         { label: 'MOU Required',        tone: 'neutral', icon: FileSignature },
};

export const ClassificationBadge: React.FC<{
  classification: GeoClassification;
  className?: string;
}> = ({ classification, className = '' }) => {
  const m = CLASS_META[classification];
  const Icon = m.icon;

  return (
    <Badge
      tone={m.tone}
      icon={<Icon className="h-3 w-3" aria-hidden="true" />}
      className={className}
    >
      {m.label}
    </Badge>
  );
};

/* ───────── Banners / notices ───────── */

export const RestrictedDataBanner: React.FC<{
  classification?: GeoClassification;
}> = ({ classification }) => (
  <Alert
    tone="warning"
    title="Restricted dataset."
    icon={<ShieldAlert className="h-4 w-4" aria-hidden="true" />}
  >
    Visibility is limited
    {classification ? (
      <> to <em>{CLASS_META[classification].label}</em></>
    ) : null}
    . Access is controlled by GIISDEC.
  </Alert>
);

export const NdaRequiredNotice: React.FC<{ signed?: boolean }> = ({ signed }) => (
  <Alert
    tone={signed ? 'success' : 'warning'}
    title={signed ? 'NDA on file.' : 'NDA required.'}
    icon={<FileSignature className="h-4 w-4" aria-hidden="true" />}
  >
    {signed
      ? 'NDA-gated content is accessible subject to server policy.'
      : 'Sign the NDA before downloads can be released.'}
  </Alert>
);

export const MouRequiredNotice: React.FC<{ signed?: boolean }> = ({ signed }) => (
  <Alert
    tone={signed ? 'success' : 'warning'}
    title={signed ? 'MOU executed.' : 'MOU required.'}
    icon={<FileSignature className="h-4 w-4" aria-hidden="true" />}
  >
    {signed
      ? 'Strategic-tier datasets are unlocked subject to server policy.'
      : 'A Memorandum of Understanding is required for this dataset tier.'}
  </Alert>
);

export const RestrictedDownloadNotice: React.FC<{
  reason: 'NDA' | 'MOU' | 'VERIFICATION' | 'CLASSIFICATION';
}> = ({ reason }) => {
  const msg = {
    NDA: 'Download blocked — NDA not on file.',
    MOU: 'Download blocked — MOU not on file.',
    VERIFICATION: 'Download blocked — investor verification required.',
    CLASSIFICATION:
      'Download blocked — classification exceeds your current clearance.',
  }[reason];

  return (
    <Alert
      tone="warning"
      icon={<Lock className="h-3.5 w-3.5" aria-hidden="true" />}
      className="px-3 py-2 text-xs"
    >
      {msg}{' '}
      <span className="text-eds-muted-fg">· enforced server-side</span>
    </Alert>
  );
};

/* ───────── Investor status indicator ───────── */

const STATUS_META: Record<
  InvestorStatus,
  {
    label: string;
    tone: 'neutral' | 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'gold';
  }
> = {
  public_visitor:             { label: 'Public Visitor',      tone: 'neutral' },
  registered_investor:        { label: 'Registered Investor', tone: 'info' },
  verified_investor:          { label: 'Verified Investor',   tone: 'success' },
  shortlisted_investor:       { label: 'Shortlisted Investor', tone: 'gold' },
  approved_investor:          { label: 'Approved Investor',   tone: 'success' },
  strategic_partner:          { label: 'Strategic Partner',   tone: 'primary' },
  restricted_access_investor: { label: 'Restricted Access',   tone: 'warning' },
  suspended_investor:         { label: 'Suspended',           tone: 'danger' },

  // Legacy values retained for compatibility.
  guest:                      { label: 'Guest',      tone: 'neutral' },
  pending:                    { label: 'Pending',    tone: 'warning' },
  verified:                   { label: 'Verified',   tone: 'success' },
  restricted:                 { label: 'Restricted', tone: 'warning' },
};

export const InvestorStatusIndicator: React.FC<{
  status: InvestorStatus;
  className?: string;
}> = ({ status, className = '' }) => {
  const m = STATUS_META[status] ?? STATUS_META.public_visitor;

  return (
    <Badge
      tone={m.tone}
      icon={<ShieldCheck className="h-3 w-3" aria-hidden="true" />}
      className={className}
    >
      {m.label}
    </Badge>
  );
};

/* ───────── Permitted GIS layers panel ───────── */

export const PermittedLayersPanel: React.FC<{
  layers: GisLayer[];
  visibility: Record<string, boolean>;
  onToggle: (id: string) => void;
}> = ({ layers, visibility, onToggle }) => (
  <Card className="p-5" elevation="sm">
    <h3 className="mb-3 flex items-center gap-2 font-eds-display font-semibold text-eds-fg">
      <ShieldCheck className="h-4 w-4 text-eds-emerald" aria-hidden="true" />
      Permitted GIS Layers
    </h3>

    {layers.length === 0 ? (
      <EmptyState
        title="No GIS layers granted yet."
        message="Permitted layers will appear here when GIISDEC grants access."
        icon={<Lock className="h-5 w-5" />}
        className="py-8"
      />
    ) : (
      <ul className="space-y-2 font-eds-body text-sm">
        {layers.map((l) => {
          const on = visibility[l.id] ?? true;

          return (
            <li
              key={l.id}
              className="flex items-center justify-between gap-3 border-b border-eds-border py-2"
            >
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onToggle(l.id)}
                  className="rounded border-eds-border accent-eds-primary"
                />
                <span className="truncate text-eds-fg">{l.name}</span>
              </label>

              <div className="flex shrink-0 items-center gap-1.5">
                <span className="text-xs text-eds-muted-fg">{l.category}</span>
                {l.classification && (
                  <ClassificationBadge classification={l.classification} />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    )}

    <p className="mt-3 flex items-start gap-1 font-eds-body text-xs text-eds-muted-fg">
      <Lock className="mt-0.5 h-3 w-3" aria-hidden="true" />
      Restricted layers are filtered out server-side.
    </p>
  </Card>
);

/* ───────── Geological dataset preview cards ───────── */

export const DatasetPreviewCard: React.FC<{
  dataset: GeoDataset;
  profile?: InvestorProfileSummary | null;
  onRequest?: (ds: GeoDataset) => void;
  /** G1C1: the agreement profile is loading/unavailable — show no inferred lock. */
  accessUnknown?: boolean;
}> = ({ dataset, profile, onRequest, accessUnknown = false }) => {
  const cls = dataset.classification;

  const blocked = accessUnknown ? null :
    cls === 'NDA_required'
      ? !profile?.ndaSigned
        ? 'NDA'
        : null
      : cls === 'MOU_required'
        ? !profile?.mouSigned
          ? 'MOU'
          : null
        : (
            cls === 'executive_only' ||
            cls === 'ministry_restricted' ||
            cls === 'confidential'
          )
          ? 'CLASSIFICATION'
          : null;

  return (
    <Card className="flex flex-col gap-2 p-4" elevation="sm">
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-eds-display text-sm font-semibold leading-snug text-eds-fg">
          {dataset.title}
        </h4>
        <ClassificationBadge classification={cls} />
      </div>

      <div className="flex items-center gap-2 font-eds-body text-xs text-eds-muted-fg">
        <span>{dataset.category}</span>
        {dataset.region && (
          <>
            <span>·</span>
            <span>{dataset.region}</span>
          </>
        )}
        {dataset.updatedAt && (
          <>
            <span>·</span>
            <span>Updated {dataset.updatedAt}</span>
          </>
        )}
      </div>

      {dataset.summary && (
        <p className="line-clamp-2 font-eds-body text-sm text-eds-muted-fg">
          {dataset.summary}
        </p>
      )}

      {accessUnknown && cls !== 'public' && (
        <p className="font-eds-body text-xs text-eds-muted-fg">Access status unavailable.</p>
      )}

      {blocked && (
        <RestrictedDownloadNotice
          reason={blocked as 'NDA' | 'MOU' | 'CLASSIFICATION'}
        />
      )}

      <div className="mt-1 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => onRequest?.(dataset)}
          leftIcon={<Download className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          {blocked ? 'Request access' : 'Download'}
        </Button>

        <span className="font-eds-body text-[10px] text-eds-muted-fg">
          Server enforces final policy
        </span>
      </div>
    </Card>
  );
};

/* ───────── Controlled disclosure request status ───────── */

const DISCLOSURE_TONE: Record<
  DisclosureRequestStatus['state'],
  'neutral' | 'info' | 'success' | 'warning' | 'danger'
> = {
  submitted: 'neutral',
  'in-review': 'warning',
  approved: 'success',
  rejected: 'danger',
  expired: 'neutral',
};

export const DisclosureRequestList: React.FC<{
  rows: DisclosureRequestStatus[];
}> = ({ rows }) => (
  <Card className="p-5" elevation="sm">
    <h3 className="mb-3 font-eds-display font-semibold text-eds-fg">
      Controlled Disclosure Requests
    </h3>

    {rows.length === 0 ? (
      <EmptyState
        title="No disclosure requests yet."
        message="Controlled disclosure requests will appear here when submitted."
        icon={<ShieldCheck className="h-5 w-5" />}
        className="py-8"
      />
    ) : (
      <ul className="divide-y divide-eds-border">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between gap-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate font-eds-body text-sm font-medium text-eds-fg">
                {r.datasetTitle ?? r.datasetId}
              </div>
              <div className="font-eds-body text-xs text-eds-muted-fg">
                Submitted {new Date(r.submittedAt).toLocaleDateString()}
                {r.reviewer ? ` · ${r.reviewer}` : ''}
              </div>
            </div>

            <Badge tone={DISCLOSURE_TONE[r.state]} dot>
              {r.state}
            </Badge>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

/* ───────── Investor agreement status cards ───────── */

export const InvestorAgreementStatusCards: React.FC<{
  profile: InvestorProfileSummary | null;
}> = ({ profile }) => {
  const items = [
    {
      key: 'NDA',
      signed: !!profile?.ndaSigned,
      label: 'NDA',
      desc: 'Non-Disclosure Agreement',
    },
    {
      key: 'MOU',
      signed: !!profile?.mouSigned,
      label: 'MOU',
      desc: 'Memorandum of Understanding',
    },
    {
      key: 'LOI',
      signed: !!profile?.loiSubmitted,
      label: 'LOI',
      desc: 'Letter of Intent',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {items.map((i) => (
        <Card key={i.key} className="p-4" elevation="sm">
          <div className="flex items-center justify-between gap-2">
            <span className="font-eds-display font-semibold text-eds-fg">
              {i.label}
            </span>

            <Badge
              tone={i.signed ? 'success' : 'warning'}
              icon={
                i.signed ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                )
              }
            >
              {i.signed ? 'On file' : 'Pending'}
            </Badge>
          </div>

          <div className="mt-1 font-eds-body text-xs text-eds-muted-fg">
            {i.desc}
          </div>
        </Card>
      ))}
    </div>
  );
};

/* ───────── Approval / review timeline ───────── */

const TIMELINE_ICON: Record<
  ReviewTimelineEntry['state'],
  React.ComponentType<{ className?: string }>
> = {
  pending: Clock,
  'in-progress': AlertTriangle,
  approved: CheckCircle2,
  rejected: XCircle,
};

const TIMELINE_TONE: Record<ReviewTimelineEntry['state'], string> = {
  pending: 'bg-eds-muted text-eds-muted-fg',
  'in-progress': 'bg-eds-warning/15 text-eds-earth',
  approved: 'bg-eds-success/12 text-eds-emerald-2',
  rejected: 'bg-eds-danger/12 text-eds-danger',
};

export const ReviewTimeline: React.FC<{
  entries: ReviewTimelineEntry[];
}> = ({ entries }) => {
  if (entries.length === 0) {
    return (
      <EmptyState
        title="No review activity yet."
        message="Review milestones will appear here as GIISDEC processes the application."
        icon={<Clock className="h-5 w-5" />}
        className="py-8"
      />
    );
  }

  return (
    <ol className="relative ml-3 space-y-4 border-l border-eds-border">
      {entries.map((e) => {
        const Icon = TIMELINE_ICON[e.state];

        return (
          <li key={e.id} className="ml-4">
            <span
              className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-eds-pill ${TIMELINE_TONE[e.state]}`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>

            <div className="flex items-center justify-between gap-2">
              <span className="font-eds-body text-sm font-medium text-eds-fg">
                {e.step}
              </span>
              <span className="font-eds-body text-xs text-eds-muted-fg">
                {e.at ? new Date(e.at).toLocaleDateString() : '—'}
              </span>
            </div>

            <div className="font-eds-body text-xs text-eds-muted-fg">
              {e.actor ?? 'System'} · {e.state}
            </div>

            {e.note && (
              <p className="mt-1 font-eds-body text-xs text-eds-muted-fg">
                {e.note}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};

/* ───────── Document access history ───────── */

export const DocumentAccessHistory: React.FC<{
  rows: DocumentAccessEntry[];
}> = ({ rows }) => (
  <Card className="p-5" elevation="sm">
    <h3 className="mb-3 font-eds-display font-semibold text-eds-fg">
      Document Access History
    </h3>

    {rows.length === 0 ? (
      <EmptyState
        title="No document access recorded yet."
        message="Document access activity will appear here."
        icon={<Eye className="h-5 w-5" />}
        className="py-8"
      />
    ) : (
      <ul className="divide-y divide-eds-border font-eds-body text-sm">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between gap-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate font-medium text-eds-fg">
                {r.documentTitle}
              </div>
              <div className="text-xs text-eds-muted-fg">
                {r.action}
                {r.classification ? ` · ${r.classification}` : ''}
              </div>
            </div>

            <span className="text-xs text-eds-muted-fg">
              {new Date(r.at).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);
